/**
 * THE TIME CLOCK'S ARITHMETIC, FOR THE SCREEN.
 *
 * Anthony, 2026-10-07: "make a clear log in log out and time tracker admin
 * can edit if needed."
 *
 * THE DATABASE DECIDES THE PAY. pay_clock_math() in migration 0200 is the
 * one copy of the rule that writes hours and California flags onto a
 * shift, and clock_out() and every admin edit call it. This file is the
 * same rule in TypeScript so the screen can show a running shift, preview
 * an edit before it is saved, and badge the flags on the timesheet. Its
 * tests carry the cases the SQL was checked against, so if one of the two
 * changes the other has to change in the same commit.
 *
 * The rule (from 0192):
 *   - A break of 30 minutes or more is an unpaid meal and comes off.
 *   - A shorter break stays paid and is flagged as not a lawful meal.
 *   - Over 5 hours worked needs a meal that starts by the end of the 5th
 *     hour. Up to 6 hours that can be waived.
 *   - Over 10 hours needs a second meal.
 *   - Over 14 hours is flagged to check the clock-out.
 *
 * PACIFIC. The workday is the Pacific day the shift started, and every
 * time on screen is Pacific wall clock, the same as the note the database
 * writes. Pure: no network, no DOM.
 */

import { PACIFIC, wallClockIn, ymdIn } from "./zoneclock.ts";

export type BreakSpan = { started_at: string; ended_at: string | null };

export type FlagCode = "short_break" | "no_meal" | "late_meal" | "no_second_meal" | "long_shift";
export type Flag = { code: FlagCode; text: string };

export type ShiftMath = {
  /** Under a minute of work. clock_out() drops these rather than logging them. */
  cancelled: boolean;
  /** Paid hours, rounded to two places the way numeric(6,2) rounds. */
  hours: number;
  /** Paid hours before rounding, which is what the flags are judged on. */
  worked: number;
  /** The Pacific day the shift started, "2026-10-07". */
  workedOn: string;
  mealMinutes: number;
  flags: Flag[];
  /** Exactly the note pay_clock_math() writes on the pay_hours row. */
  note: string;
};

const ms = (iso: string): number => Date.parse(iso);

/** "8:02 AM", Pacific. Postgres's FMHH12:MI AM, with a plain space. */
export function clockFace(iso: string, zone = PACIFIC): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone, hour: "numeric", minute: "2-digit", hour12: true,
  }).format(new Date(iso)).replace(/\s/g, " ");
}

/** "Thu", Pacific. Postgres's Dy. */
export function weekdayIn(iso: string, zone = PACIFIC): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, weekday: "short" }).format(new Date(iso));
}

/** The Pacific calendar day of an instant. */
export const dayOf = (iso: string, zone = PACIFIC): string => ymdIn(zone, new Date(iso));

/**
 * The hours, the day and the flags for one shift. Every break must be
 * closed; an open one is closed at the clock-out first, as clock_out() does.
 */
export function shiftMath(clockIn: string, clockOut: string, breaks: BreakSpan[], zone = PACIFIC): ShiftMath {
  const inMs = ms(clockIn);
  const outMs = ms(clockOut);
  const sorted = [...breaks]
    .map((b) => ({ s: ms(b.started_at), e: b.ended_at ? ms(b.ended_at) : outMs }))
    .sort((a, b) => a.s - b.s);

  let mealSecs = 0;
  let meals = 0;
  let firstMealAtWork: number | null = null;
  let shortBreaks = 0;
  let unpaidBefore = 0;
  for (const b of sorted) {
    const secs = (b.e - b.s) / 1000;
    if (secs >= 1800) {
      meals += 1;
      if (firstMealAtWork === null) firstMealAtWork = ((b.s - inMs) / 1000 - unpaidBefore) / 3600;
      mealSecs += secs;
      unpaidBefore += secs;
    } else {
      shortBreaks += 1;
    }
  }

  const workedMs = (outMs - inMs) - mealSecs * 1000;
  const worked = workedMs / 3600000;
  const hours = round2(workedMs);
  const workedOn = dayOf(clockIn, zone);
  const mealMinutes = Math.round(mealSecs / 60);

  if (hours < 0.02) {
    return { cancelled: true, hours: 0, worked, workedOn, mealMinutes, flags: [], note: "" };
  }

  const flags: Flag[] = [];
  if (shortBreaks > 0) {
    flags.push({
      code: "short_break",
      text: `${shortBreaks}${shortBreaks === 1 ? " break was" : " breaks were"} under 30 minutes, so paid and not a lawful meal break`,
    });
  }
  if (worked > 5 && (firstMealAtWork === null || firstMealAtWork > 5)) {
    flags.push({
      code: firstMealAtWork === null ? "no_meal" : "late_meal",
      text: (firstMealAtWork === null ? "No meal break" : "Meal break started after the 5th hour")
        + (worked <= 6 ? " (can be waived when the shift is 6 hours or less)" : ", a meal premium may be owed"),
    });
  }
  if (worked > 10 && meals < 2) flags.push({ code: "no_second_meal", text: "No second meal break after 10 hours" });
  if (worked > 14) flags.push({ code: "long_shift", text: "Long shift, check the clock-out" });

  const outDay = dayOf(clockOut, zone);
  const note = `Clocked in ${clockFace(clockIn, zone)}, out `
    + (outDay !== workedOn ? `${weekdayIn(clockOut, zone)} ${clockFace(clockOut, zone)}` : clockFace(clockOut, zone))
    + " Pacific"
    + (mealSecs > 0 ? `, ${mealMinutes} min meal break unpaid` : "")
    + (flags.length ? `. ${flags.map((f) => f.text).join(". ")}` : "");

  return { cancelled: false, hours, worked, workedOn, mealMinutes, flags, note };
}

/**
 * Hours to two places from milliseconds. Postgres rounds numeric half away
 * from zero. A hundredth of an hour is 36,000 ms, so dividing whole
 * milliseconds by that lands exactly on .5 in the half cases, where
 * multiplying a float of hours by 100 would not.
 */
function round2(workedMs: number): number {
  const cents = workedMs / 36000;
  return (Math.sign(cents) * Math.round(Math.abs(cents))) / 100;
}

/** The short badge text for a flag, for a row that has no room for a sentence. */
export const FLAG_BADGE: Record<FlagCode, string> = {
  short_break: "Short break",
  no_meal: "No meal",
  late_meal: "Late meal",
  no_second_meal: "No 2nd meal",
  long_shift: "Long shift",
};

/** A badge that can be waived reads softer than one that may owe a premium. */
export const flagTone = (f: Flag): "warn" | "bad" =>
  f.code === "short_break" || f.text.includes("can be waived") ? "warn" : "bad";

/* ── reading the times a person types ───────────────────────────────── */

/**
 * The instant a Pacific wall time names, from a day and "HH:MM".
 * Null when either is missing or malformed.
 */
export function wallTime(day: string, hhmm: string, zone = PACIFIC): string | null {
  if (!/^\d{4}-\d\d-\d\d$/.test(day)) return null;
  const m = /^(\d\d?):(\d\d)$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]); const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return wallClockIn(zone, `${day}T${String(h).padStart(2, "0")}:${m[2]}:00`).toISOString();
}

const nextDay = (day: string): string => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** "HH:MM" of an instant in the zone, for a time input. */
export function hhmmOf(iso: string, zone = PACIFIC): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const h = parts.find((p) => p.type === "hour")?.value ?? "00";
  const m = parts.find((p) => p.type === "minute")?.value ?? "00";
  return `${h === "24" ? "00" : h}:${m}`;
}

export type TypedShift = {
  day: string;
  clockIn: string;
  /** Empty when the shift is still going. */
  clockOut: string;
  breaks: Array<{ start: string; end: string }>;
};

export type ResolvedShift = {
  clock_in: string;
  clock_out: string | null;
  breaks: BreakSpan[];
};

/**
 * Times typed on the edit form, read as one shift that starts on `day`.
 * A time earlier than the clock-in is the next morning, so a shift that
 * ran past midnight is typed the way it reads on a watch. Returns a reason
 * in plain words when something cannot be read.
 */
export function resolveShift(t: TypedShift, zone = PACIFIC): ResolvedShift | { error: string } {
  const cin = wallTime(t.day, t.clockIn, zone);
  if (!cin) return { error: "Pick the day and the clock-in time." };
  const at = (hhmm: string): string | null => {
    const same = wallTime(t.day, hhmm, zone);
    if (!same) return null;
    return ms(same) < ms(cin) ? wallTime(nextDay(t.day), hhmm, zone) : same;
  };
  let cout: string | null = null;
  if (t.clockOut.trim()) {
    cout = at(t.clockOut);
    if (!cout) return { error: "The clock-out time is not a time." };
    if (ms(cout) === ms(cin)) return { error: "Clock-out has to be after clock-in." };
  }
  const breaks: BreakSpan[] = [];
  for (const b of t.breaks) {
    if (!b.start.trim() && !b.end.trim()) continue;
    const s = at(b.start);
    if (!s) return { error: "Every meal break needs a start time." };
    let e: string | null = null;
    if (b.end.trim()) {
      e = at(b.end);
      if (!e) return { error: "A meal break end is not a time." };
    } else if (cout) {
      return { error: "Every meal break needs an end time." };
    }
    breaks.push({ started_at: s, ended_at: e });
  }
  breaks.sort((a, b) => ms(a.started_at) - ms(b.started_at));
  return { clock_in: cin, clock_out: cout, breaks };
}

/* ── today's punches, as a list a person can read ───────────────────── */

export type Punch = {
  id: string;
  technician_id: string;
  clock_in: string;
  clock_out: string | null;
  breaks: BreakSpan[];
};

export type PunchLine = { label: "In" | "Meal" | "Out"; text: string; live?: boolean };

/**
 * "In 8:02 AM", "Meal 12:00 PM to 12:31 PM", "Out 4:45 PM", oldest first,
 * for every punch that started on `day`. A break still going reads "Meal
 * 12:00 PM, still on it" and an open shift has no Out line yet.
 */
export function punchLines(punches: Punch[], day: string, zone = PACIFIC): PunchLine[] {
  const out: PunchLine[] = [];
  const todays = punches.filter((p) => dayOf(p.clock_in, zone) === day)
    .sort((a, b) => ms(a.clock_in) - ms(b.clock_in));
  for (const p of todays) {
    out.push({ label: "In", text: clockFace(p.clock_in, zone) });
    for (const b of [...p.breaks].sort((a, c) => ms(a.started_at) - ms(c.started_at))) {
      out.push(b.ended_at
        ? { label: "Meal", text: `${clockFace(b.started_at, zone)} to ${clockFace(b.ended_at, zone)}` }
        : { label: "Meal", text: `${clockFace(b.started_at, zone)}, still on it`, live: true });
    }
    if (p.clock_out) out.push({ label: "Out", text: clockFace(p.clock_out, zone) });
  }
  return out;
}

/** "3:07:42", hours unpadded, for the running timer. */
export function runningClock(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Seconds of paid work so far on an open shift: the span to now, less any
 * finished meal of 30 minutes or more, less a break still going (it is
 * off the clock while it runs, and the person can see the shift pause).
 */
export function workedSoFar(p: Punch, now: number): number {
  let secs = (now - ms(p.clock_in)) / 1000;
  for (const b of p.breaks) {
    if (!b.ended_at) { secs -= (now - ms(b.started_at)) / 1000; continue; }
    const len = (ms(b.ended_at) - ms(b.started_at)) / 1000;
    if (len >= 1800) secs -= len;
  }
  return Math.max(0, secs);
}

/* ── the trail ──────────────────────────────────────────────────────── */

export type Snapshot = {
  clock_in: string;
  clock_out: string | null;
  breaks: BreakSpan[];
  hours: number | null;
  worked_on: string | null;
  note: string | null;
};

export type ClockEdit = {
  id: string;
  technician_id: string;
  clock_id: string | null;
  action: "edit" | "clock_out" | "add" | "delete";
  editor_name: string | null;
  edited_at: string;
  before: Snapshot | null;
  after: Snapshot | null;
  reason: string;
};

const mealsText = (bs: BreakSpan[], zone: string): string =>
  bs.length
    ? bs.map((b) => `${clockFace(b.started_at, zone)} to ${b.ended_at ? clockFace(b.ended_at, zone) : "still going"}`).join(", ")
    : "none";

/** "Oct 7", Pacific. */
const shortDate = (iso: string, zone: string): string =>
  new Intl.DateTimeFormat("en-US", { timeZone: zone, month: "short", day: "numeric" }).format(new Date(iso));

/** What changed, one plain line per thing, old value first. */
export function changeLines(before: Snapshot | null, after: Snapshot | null, zone = PACIFIC): string[] {
  if (!before || !after) return [];
  const lines: string[] = [];
  if (ms(before.clock_in) !== ms(after.clock_in)) {
    lines.push(`In was ${clockFace(before.clock_in, zone)}, now ${clockFace(after.clock_in, zone)}`);
  }
  const bo = before.clock_out ? ms(before.clock_out) : null;
  const ao = after.clock_out ? ms(after.clock_out) : null;
  if (bo !== ao) {
    lines.push(`Out was ${before.clock_out ? clockFace(before.clock_out, zone) : "never punched"}, now ${
      after.clock_out ? clockFace(after.clock_out, zone) : "still going"}`);
  }
  const bm = mealsText(before.breaks ?? [], zone);
  const am = mealsText(after.breaks ?? [], zone);
  if (bm !== am) lines.push(`Meal breaks were ${bm}, now ${am}`);
  if (before.hours != null && after.hours != null && Number(before.hours) !== Number(after.hours)) {
    lines.push(`Hours were ${Number(before.hours).toFixed(2)}, now ${Number(after.hours).toFixed(2)}`);
  }
  return lines;
}

/** The headline for one trail row, who and when. */
export function editHeadline(e: ClockEdit, zone = PACIFIC): string {
  const who = e.editor_name?.trim() || "the desk";
  const when = shortDate(e.edited_at, zone);
  switch (e.action) {
    case "add": return `Added by ${who} on ${when}`;
    case "delete": return `Deleted by ${who} on ${when}`;
    case "clock_out": return `Clocked out by ${who} on ${when}`;
    default: return `Edited by ${who} on ${when}`;
  }
}

/** A deleted shift, said in one line from what it was. */
export function deletedLine(e: ClockEdit, zone = PACIFIC): string {
  const b = e.before;
  if (!b) return "A shift was deleted.";
  return `It was in ${clockFace(b.clock_in, zone)}, out ${b.clock_out ? clockFace(b.clock_out, zone) : "never punched"}${
    b.hours != null ? `, ${Number(b.hours).toFixed(2)} hours` : ""}.`;
}
